import { World, WorldCategory, WorldManifestEntry, WorldsManifest } from '../types/world';
import { validateWorld } from '../utils/validators';

const WORLDS_CACHE = new Map<string, World>();

export function getWorldId(entry: WorldManifestEntry): string {
  // Explicit IDs support nested paths; retain the legacy world0-tutorial convention.
  return entry.id ?? entry.file.split('/').pop()!.replace(/\.json$/, '').split('-')[0];
}

export async function loadWorld(worldId: string, manifest?: WorldsManifest): Promise<World | null> {
  // Check cache first
  if (WORLDS_CACHE.has(worldId)) {
    return WORLDS_CACHE.get(worldId)!;
  }

  try {
    // Get filename from manifest
    const catalog = manifest ?? await loadWorldsManifest();
    const worldInfo = catalog.worlds.find(w => getWorldId(w) === worldId);
    if (!worldInfo) {
      throw new Error(`World ${worldId} not found in manifest`);
    }
    
    const filename = worldInfo.file;
    // Public JSON filenames are not fingerprinted, so revalidate after a deployment.
    const response = await fetch(`/worlds/${filename}`, { cache: 'no-cache' });
    
    if (!response.ok) {
      throw new Error(`Failed to load world: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!validateWorld(data)) {
      throw new Error('Invalid world data structure');
    }
    if (data.id !== worldId) {
      throw new Error(`World ID ${data.id} does not match manifest ID ${worldId}`);
    }
    
    WORLDS_CACHE.set(worldId, data);
    return data;
  } catch (error) {
    console.error(`Error loading world ${worldId}:`, error);
    return null;
  }
}

export async function loadWorldsManifest(): Promise<WorldsManifest> {
  const response = await fetch('/config/worlds-manifest.json', { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error('Failed to load worlds manifest');
  }
  const data = await response.json();
  if (!data || !Array.isArray(data.worlds) || !data.worlds.every((entry: WorldManifestEntry) =>
    entry && typeof entry.file === 'string' && entry.file.length > 0 &&
    typeof entry.enabled === 'boolean' &&
    (entry.id === undefined || (typeof entry.id === 'string' && entry.id.length > 0))
  )) {
    throw new Error('Invalid worlds manifest');
  }
  const worldIds = new Set<string>(data.worlds.map(getWorldId));
  if (worldIds.size !== data.worlds.length) throw new Error('Duplicate world IDs in manifest');
  if (data.categories !== undefined) {
    const categoryIds = new Set<string>();
    if (!Array.isArray(data.categories) || !data.categories.every((category: WorldCategory) => {
      if (!category || typeof category.id !== 'string' || !/^[a-z0-9][a-z0-9_-]*$/.test(category.id) ||
          categoryIds.has(category.id) || typeof category.name !== 'string' || !category.name.trim() ||
          !Array.isArray(category.worlds) || new Set(category.worlds).size !== category.worlds.length ||
          !category.worlds.every(id => typeof id === 'string' && worldIds.has(id))) return false;
      categoryIds.add(category.id);
      const optionalText = ['description', 'detailsDescription', 'icon', 'institution', 'institutionShortName', 'academicYear'] as const;
      return optionalText.every(key => category[key] === undefined || typeof category[key] === 'string') &&
        (category.logo === undefined || (typeof category.logo === 'string' &&
          /^\/(?!\/)/.test(category.logo) && !category.logo.split('/').includes('..'))) &&
        (category.website === undefined || (typeof category.website === 'string' && /^https?:\/\//.test(category.website)));
    })) throw new Error('Invalid world categories');
  }
  return data;
}

export function clearWorldsCache(): void {
  WORLDS_CACHE.clear();
}
